import { CheckCircleRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import {
    KboAnalysisDimensionResult,
    KboCohortDefinition,
    KboMultiLevelAnalysisPass,
    KboMultiLevelAnalysisResult,
    KboMultiLevelFinding,
} from "./KboFlowRunDebug";
import {
    formatDate,
    formatNumber,
    renderable,
    SectionStack,
} from "./KboFlowDebugUI";

function formatPercent(value?: number): string {
    if (typeof value !== "number") return "—";
    return new Intl.NumberFormat(undefined, {
        style: "percent",
        maximumFractionDigits: 1,
    }).format(value);
}

/** Turns a camelCase/PascalCase code into a readable phrase, e.g. "modelStopped" -> "Model stopped". */
function humanizeCode(value?: string): string {
    if (!value) return "";
    const spaced = value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Raw (unstringified) value behind a dimension-value result, before display formatting. */
function rawDimensionValue(
    pass: KboMultiLevelAnalysisPass,
    result: KboAnalysisDimensionResult,
    index: number,
): unknown {
    if (result.distribution?.value !== undefined) {
        return result.distribution.value;
    }
    const dimensionAlias = pass.dimension?.alias;
    const matchingFilter = result.cohort?.filters?.find(
        (filter) => filter.alias === dimensionAlias,
    );
    return matchingFilter?.value ?? `Result ${index + 1}`;
}

/** Formats a dimension value for display, rendering numeric [min, max] pairs as a readable range. */
function formatDimensionValueLabel(value: unknown): string {
    if (Array.isArray(value)) {
        const pair = Array.isArray(value[0]) ? value[0] : value;
        if (
            pair.length === 2 &&
            pair.every((entry) => typeof entry === "number")
        ) {
            return `${formatNumber(pair[0])}–${formatNumber(pair[1])}`;
        }
        return value.map((entry) => String(entry)).join(", ");
    }
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

/** Index of the dimension-value result containing the pass's selected finding, or -1. */
function selectedResultIndex(pass: KboMultiLevelAnalysisPass): number {
    const selectedId = pass.selection?.findingId;
    if (!selectedId) return -1;
    return (pass.results ?? []).findIndex((result) =>
        (result.findings ?? []).some(
            (finding) => finding.findingId === selectedId,
        ),
    );
}

/** Small uppercase caption above a group of related facts, matching the "STARTING COHORT" label style. */
function LabeledGroup({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <Box>
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{children}</Box>
        </Box>
    );
}

/* -------------------------------- cohort facts ----------------------------- */

function CohortFacts({
    cohort,
    count,
}: {
    cohort?: KboCohortDefinition;
    count?: number;
}) {
    const filters = cohort?.filters ?? [];
    const dimensions = cohort?.dimensions ?? [];
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {typeof count === "number" && (
                    <LabeledGroup label="Conversations">
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={formatNumber(count)}
                        />
                    </LabeledGroup>
                )}
                {(cohort?.startDateUtc || cohort?.endDateUtc) && (
                    <LabeledGroup label="Date range">
                        <Typography variant="body2">
                            {`${formatDate(
                                cohort?.startDateUtc,
                            )} → ${formatDate(cohort?.endDateUtc)}`}
                        </Typography>
                    </LabeledGroup>
                )}
            </Box>
            {filters.length > 0 && (
                <LabeledGroup label="Filters">
                    <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                    >
                        {filters.map((filter, index) => {
                            const chip = (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${filter.alias ?? "—"} ${
                                        filter.operator ?? ""
                                    } ${
                                        filter.value === undefined
                                            ? ""
                                            : String(filter.value)
                                    }`.trim()}
                                />
                            );
                            return filter.reason ? (
                                <Tooltip key={index} title={filter.reason}>
                                    {chip}
                                </Tooltip>
                            ) : (
                                <Box key={index}>{chip}</Box>
                            );
                        })}
                    </Box>
                </LabeledGroup>
            )}
            {dimensions.length > 0 && (
                <LabeledGroup label="Dimensions">
                    <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                    >
                        {dimensions.map((dimension, index) => (
                            <Chip
                                key={index}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                label={dimension.alias ?? "—"}
                            />
                        ))}
                    </Box>
                </LabeledGroup>
            )}
        </Box>
    );
}

/* ---------------------------------- finding --------------------------------- */

function FindingBlock({
    finding,
    prominent,
}: {
    finding: KboMultiLevelFinding;
    prominent?: boolean;
}) {
    const repCount = finding.representativeConversationIds?.length ?? 0;
    return (
        <Box>
            <Typography
                variant="body2"
                sx={{ fontWeight: prominent ? 700 : 500 }}
            >
                {renderable(finding.claim)}
            </Typography>
            {finding.reasoning && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {finding.reasoning}
                </Typography>
            )}
            {repCount > 0 && (
                <Chip
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.75 }}
                    label={`${repCount} representative conversation${
                        repCount === 1 ? "" : "s"
                    }`}
                />
            )}
        </Box>
    );
}

/* ----------------------------- dimension value ------------------------------ */

function DimensionValueBlock({
    pass,
    result,
    resultIndex,
    selected,
}: {
    pass: KboMultiLevelAnalysisPass;
    result: KboAnalysisDimensionResult;
    resultIndex: number;
    selected: boolean;
}) {
    const findings = result.findings ?? [];
    const selectedId = pass.selection?.findingId;
    const selectedFinding = findings.find(
        (finding) => finding.findingId === selectedId,
    );
    const otherFindings = findings.filter(
        (finding) => finding.findingId !== selectedId,
    );
    const label = formatDimensionValueLabel(
        rawDimensionValue(pass, result, resultIndex),
    );
    const share = result.distribution?.share;
    const reviewedMaterialCount =
        result.reviewedMaterialCount ?? result.analysisMaterial?.length ?? 0;
    const reviewedMaterialCoverage = result.reviewedMaterialCoverage;
    const countLabel = `${formatNumber(result.distribution?.count)} in cohort${
        typeof share === "number" ? ` · ${formatPercent(share)} share` : ""
    }`;

    if (!selected) {
        // Not the chosen dimension value for this pass — kept small and fully
        // collapsed by default, but still available to inspect.
        return (
            <Accordion
                disableGutters
                sx={{
                    "&:before": { display: "none" },
                    backgroundColor: "transparent",
                    border: "1px solid",
                    borderColor: "lightgrayBorder.main",
                    borderRadius: "4px !important",
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreRounded fontSize="small" />}
                    sx={{
                        minHeight: 36,
                        "& .MuiAccordionSummary-content": { my: 0.5 },
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={countLabel}
                        />
                        {findings.length > 0 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${findings.length} finding${
                                    findings.length === 1 ? "" : "s"
                                }`}
                            />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {findings.length === 0 ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            No findings recorded.
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {findings.map((finding, index) => (
                                <FindingBlock
                                    key={finding.findingId ?? index}
                                    finding={finding}
                                />
                            ))}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Paper
            variant="outlined"
            sx={{ borderColor: "success.main", borderWidth: 2, p: 1.5 }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mb: 1,
                }}
            >
                <CheckCircleRounded color="success" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {label}
                </Typography>
                <Chip size="small" variant="outlined" label={countLabel} />
                <Chip
                    size="small"
                    variant="outlined"
                    label={`${formatNumber(reviewedMaterialCount)} reviewed`}
                />
                {typeof reviewedMaterialCoverage === "number" && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatPercent(reviewedMaterialCoverage)} reviewed`}
                    />
                )}
            </Box>
            {selectedFinding ? (
                <FindingBlock finding={selectedFinding} prominent />
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No finding was selected for this dimension value.
                </Typography>
            )}
            {otherFindings.length > 0 && (
                <Accordion
                    disableGutters
                    sx={{
                        "&:before": { display: "none" },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px !important",
                        mt: 1.5,
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreRounded fontSize="small" />}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {otherFindings.length} other finding
                            {otherFindings.length === 1 ? "" : "s"}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {otherFindings.map((finding, index) => (
                                <FindingBlock
                                    key={finding.findingId ?? index}
                                    finding={finding}
                                />
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}
        </Paper>
    );
}

/* ------------------------------------ pass ----------------------------------- */

function PassStep({
    pass,
    stepNumber,
}: {
    pass: KboMultiLevelAnalysisPass;
    stepNumber: number;
}) {
    const results = pass.results ?? [];
    const selIndex = selectedResultIndex(pass);

    const shouldContinue = pass.selection?.shouldContinue;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Chip
                    size="small"
                    color="primary"
                    label={`Step ${stepNumber}`}
                />
                <LabeledGroup label="Selected dimension">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {pass.dimension?.alias ?? "Dimension not recorded"}
                    </Typography>
                </LabeledGroup>
            </Box>
            {pass.dimension?.reason && (
                <LabeledGroup label="Why this dimension">
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                    >
                        {pass.dimension.reason}
                    </Typography>
                </LabeledGroup>
            )}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    mt: 0.5,
                }}
            >
                {results.map((result, resultIndex) => (
                    <DimensionValueBlock
                        key={resultIndex}
                        pass={pass}
                        result={result}
                        resultIndex={resultIndex}
                        selected={resultIndex === selIndex}
                    />
                ))}
                {results.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No dimension values recorded.
                    </Typography>
                )}
            </Box>
            {pass.selection?.reason && (
                <LabeledGroup
                    label={
                        shouldContinue
                            ? "Why the analysis went deeper from here"
                            : "Why the analysis stopped here"
                    }
                >
                    <Typography variant="body2" color="text.secondary">
                        {pass.selection.reason}
                    </Typography>
                </LabeledGroup>
            )}
        </Box>
    );
}

/* --------------------------------- question ---------------------------------- */

function QuestionJourneyCard({
    analysis,
    questionIndex,
}: {
    analysis: KboMultiLevelAnalysisResult;
    questionIndex: number;
}) {
    const sortedPasses = useMemo(
        () =>
            (analysis.passes ?? [])
                .map((pass, originalIndex) => ({ pass, originalIndex }))
                .sort((a, b) => {
                    const levelDifference =
                        (a.pass.level ?? Number.MAX_SAFE_INTEGER) -
                        (b.pass.level ?? Number.MAX_SAFE_INTEGER);
                    return (
                        levelDifference || a.originalIndex - b.originalIndex
                    );
                }),
        [analysis.passes],
    );
    const finalFinding = analysis.finalFinding;
    const repCount = finalFinding?.representativeConversationIds?.length ?? 0;

    return (
        <Card variant="outlined">
            <CardContent>
                <Chip
                    size="small"
                    color="primary"
                    label={`Question ${questionIndex + 1}`}
                    sx={{ mb: 1 }}
                />
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, lineHeight: 1.3 }}
                >
                    {analysis.question?.text ?? "Question not recorded"}
                </Typography>
                {analysis.question?.rationale && (
                    <Box sx={{ mb: 2, mt: 1.5 }}>
                        <LabeledGroup label="Rationale">
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {analysis.question.rationale}
                            </Typography>
                        </LabeledGroup>
                    </Box>
                )}

                <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
                    <Typography variant="overline" color="text.secondary">
                        Starting cohort
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                        <CohortFacts
                            cohort={analysis.initialCohort}
                            count={analysis.initialCohortCount}
                        />
                        {analysis.initialCohort?.rationale && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {analysis.initialCohort.rationale}
                            </Typography>
                        )}
                    </Box>
                </Paper>

                <Box
                    sx={{
                        borderLeft: "2px solid",
                        borderColor: "primary.light",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        ml: 1,
                        mt: 2,
                        pl: 2,
                    }}
                >
                    {sortedPasses.map(
                        ({ pass, originalIndex }, displayIndex) => (
                            <PassStep
                                key={pass.passId ?? originalIndex}
                                pass={pass}
                                stepNumber={displayIndex + 1}
                            />
                        ),
                    )}
                    {sortedPasses.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No analysis passes recorded.
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Paper
                    variant="outlined"
                    sx={{
                        borderColor: "success.main",
                        borderLeftWidth: 4,
                        p: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            mb: 1,
                        }}
                    >
                        <CheckCircleRounded color="success" />
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                        >
                            Final answer
                        </Typography>
                        {analysis.completionReason && (
                            <Tooltip title={analysis.completionReason}>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={humanizeCode(
                                        analysis.completionReason,
                                    )}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    {finalFinding ? (
                        <>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                            >
                                {renderable(finalFinding.claim)}
                            </Typography>
                            {finalFinding.reasoning && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    {finalFinding.reasoning}
                                </Typography>
                            )}
                            {repCount > 0 && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                    label={`${repCount} representative conversation${
                                        repCount === 1 ? "" : "s"
                                    }`}
                                />
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No final finding recorded.
                        </Typography>
                    )}
                </Paper>
            </CardContent>
        </Card>
    );
}

export function KboFlowAnalysisJourneySection({
    results,
}: {
    results: KboMultiLevelAnalysisResult[];
}) {
    if (results.length === 0) {
        return <Typography variant="body2">No analysis results.</Typography>;
    }

    return (
        <SectionStack>
            {results.map((analysis, index) => (
                <QuestionJourneyCard
                    key={analysis.question?.id ?? index}
                    analysis={analysis}
                    questionIndex={index}
                />
            ))}
        </SectionStack>
    );
}
